import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Upload, X, Music, Image as ImageIcon, Mail, Info, AlertCircle, CheckCircle, Loader2, AlertTriangle, Search, RefreshCw, ListOrdered, Image, Download, FolderOpen, BarChart3, ArrowLeft } from "lucide-react";
import toast, { Toaster } from "react-hot-toast";
import digikoreLogo from "../../assets/digikorestudios.png";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from 'react-router-dom';

import { createCollage, getAllCollage } from "../../redux/reduxSlices/collageSlice";
import Header from "../common/Header";

const CollageCreationPage = ({ user, onLogout }) => {
    const dispatch = useDispatch();
    const { getAllCollageResponse, loading } = useSelector((state) => state.collage);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    const [images, setImages] = useState([]);
    const [brandLogo, setBrandLogo] = useState([]);
    const [audioFile, setAudioFile] = useState(null);
    const [orders, setOrders] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [isLoadingOrders, setIsLoadingOrders] = useState(false);
    const navigate = useNavigate();
    const [uploadProgress, setUploadProgress] = useState({});

    useEffect(() => {
        dispatch(getAllCollage())
    }, [])
    useEffect(() => {
        if (getAllCollageResponse?.status == "success") {
            setOrders(getAllCollageResponse.orders)
        }
    }, [getAllCollageResponse])

    const filteredOrders = orders.filter(order =>
        order.company_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        order.email_id.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
        watch
    } = useForm({
        defaultValues: {
            companyName: "",
            email: "",
        }
    });

    const companyName = watch("companyName");
    const email = watch("email");

    // Handle image upload
    const handleImageUpload = (e) => {
        const files = Array.from(e.target.files);

        // Check total images limit
        if (images.length + files.length > 25) {
            toast.error("Maximum 25 images allowed");
            return;
        }

        const validFiles = files.filter(file => {
            const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
            const isValidType = validTypes.includes(file.type);
            const isValidSize = file.size <= 20 * 1024 * 1024;

            if (!isValidType) {
                toast.error(`${file.name}: Invalid file type. Only JPG, JPEG, PNG, WEBP, HEIC, HEIF allowed.`);
                return false;
            }
            if (!isValidSize) {
                toast.error(`${file.name}: File size must be between 0.5MB and 6MB.`);
                return false;
            }

            return true;
        });

        // Create preview URLs
        const newImages = validFiles.map(file => ({
            id: Date.now() + Math.random(),
            file,
            preview: URL.createObjectURL(file),
            name: file.name,
            size: (file.size / (1024 * 1024)).toFixed(2) + ' MB'
        }));

        setImages(prev => [...prev, ...newImages]);

        if (validFiles.length > 0) {
            toast.success(`Added ${validFiles.length} image(s)`);
        }
    };

    const handleBrandLogoUpload = (e) => {
        const files = Array.from(e.target.files);

        // Check total images limit
        if (brandLogo.length + files.length > 25) {
            toast.error("Maximum 1 image allowed");
            return;
        }

        const validFiles = files.filter(file => {
            const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
            const isValidType = validTypes.includes(file.type);
            const isValidSize = file.size <= 20 * 1024 * 1024;

            if (!isValidType) {
                toast.error(`${file.name}: Invalid file type. Only JPG, JPEG, PNG, WEBP, HEIC, HEIF allowed.`);
                return false;
            }
            if (!isValidSize) {
                toast.error(`${file.name}: File size must be between 0.5MB and 6MB.`);
                return false;
            }

            return true;
        });

        // Create preview URLs
        const newImages = validFiles.map(file => ({
            id: Date.now() + Math.random(),
            file,
            preview: URL.createObjectURL(file),
            name: file.name,
            size: (file.size / (1024 * 1024)).toFixed(2) + ' MB'
        }));

        setBrandLogo(newImages);

        if (validFiles.length > 0) {
            toast.success(`Added ${validFiles.length} image(s)`);
        }
    };

    // Handle audio upload
    const handleAudioUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const validTypes = ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/x-wav'];
        const maxSize = 30 * 1024 * 1024; // 30MB

        if (!validTypes.includes(file.type)) {
            toast.error("Invalid audio file. Only MP3 and WAV files allowed.");
            return;
        }

        if (file.size > maxSize) {
            toast.error("Audio file size should not exceed 30MB.");
            return;
        }

        setAudioFile({
            file,
            name: file.name,
            size: (file.size / (1024 * 1024)).toFixed(2) + ' MB',
            preview: URL.createObjectURL(file)
        });

        toast.success("Audio file uploaded successfully");
    };

    // Remove image
    const removeImage = (id) => {
        setImages(prev => {
            const imageToRemove = prev.find(img => img.id === id);
            if (imageToRemove?.preview) {
                URL.revokeObjectURL(imageToRemove.preview);
            }
            return prev.filter(img => img.id !== id);
        });
        toast.success("Image removed");
    };

    const removeLogo = (id) => {
        setBrandLogo(prev => {
            const imageToRemove = prev.find(img => img.id === id);
            if (imageToRemove?.preview) {
                URL.revokeObjectURL(imageToRemove.preview);
            }
            return prev.filter(img => img.id !== id);
        });
        toast.success("Image removed");
    };

    // Remove audio
    const removeAudio = () => {
        if (audioFile?.preview) {
            URL.revokeObjectURL(audioFile.preview);
        }
        setAudioFile(null);
        toast.success("Audio file removed");
    };

    // Clear all images
    const clearAllImages = () => {
        images.forEach(img => {
            if (img.preview) {
                URL.revokeObjectURL(img.preview);
            }
        });
        setImages([]);
        toast.success("All images cleared");
    };
    const clearLogo = () => {
        brandLogo.forEach(img => {
            if (img.preview) {
                URL.revokeObjectURL(img.preview);
            }
        });
        setBrandLogo([]);
        toast.success("All images cleared");
    };

    // Handle form submission
    const onSubmit = async (data) => {
        // Validate minimum images
        if (images.length < 5) {
            toast.error("Minimum 5 images required");
            return;
        }

        if (!audioFile) {
            toast.error("Audio file is required");
            return;
        }

        setIsLoading(true);
        setError(null);
        const formData = new FormData();
        formData.append("company_name", data.companyName);
        formData.append("email_id", data.email);
        formData.append("is_repeated", data.isRepetative);
        if (brandLogo?.[0]?.file) {
            formData.append("company_logo", brandLogo[0].file);
        }

        images.forEach((img) => formData.append("images", img.file));

        // audio
        formData.append("song", audioFile.file);

        // Debug: Log FormData content
        // for (let [key, value] of formData.entries()) {
        //     console.log(`${key}:`, value instanceof File ? `${value.name} (${value.type})` : value);
        // }
        // console.log("formdata", formData)
        // return
        try {
            const response = await dispatch(createCollage(formData)).unwrap();
            console.log("res", response)
            if (response.status == "success") {
                await dispatch(getAllCollage())
                setUploadProgress({});

                // Show success
                setSuccess(true);
                toast.success(
                    <div>
                        <p className="font-semibold">Collage creation in progress!</p>
                        <p className="text-sm">Check your email for further updates.</p>
                    </div>,
                    { duration: 5000 }
                );
            }

            // Reset form
            reset();
            setImages([]);
            setBrandLogo([]);
            setAudioFile(null);

        } catch (err) {
            setError(err.message || "Failed to create collage");
            toast.error(err.message || "Failed to create collage");
            setUploadProgress({});
        } finally {
            setIsLoading(false);
        }
    };

    const handleRefresh = async () => {
        try {
            setIsLoadingOrders(true);
            await dispatch(getAllCollage()).unwrap();
            toast.success("Orders refreshed");
        } catch (err) {
            toast.error("Failed to refresh orders");
        } finally {
            setIsLoadingOrders(false);
        }
    };


    return (
        <>
            <Header user={user} onLogout={onLogout} />
            <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-3 md:p-4">
                <Toaster
                    position="top-right"
                    toastOptions={{
                        duration: 3000,
                        style: {
                            background: '#363636',
                            color: '#fff',
                            padding: '12px 16px',
                            borderRadius: '8px',
                        },
                        success: {
                            duration: 3000,
                            iconTheme: {
                                primary: '#10B981',
                                secondary: '#fff',
                            },
                        },
                        error: {
                            duration: 4000,
                            iconTheme: {
                                primary: '#EF4444',
                                secondary: '#fff',
                            },
                        },
                    }}
                />

                <div className="w-full mx-auto">
                    {/* Header - Reduced padding */}
                    <div className="flex items-center gap-3 max-md:w-full max-md:mb-3 max-md:justify-between mb-3">
                        <button
                            onClick={() => navigate(-1)}
                            className="flex items-center gap-1.5 text-gray-600 hover:text-gray-800 transition-colors text-sm"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            <span className="font-medium">Back</span>
                        </button>
                        <div>
                            <h1 className="text-lg font-bold text-gray-900">
                                Create Your Collage
                            </h1>
                            <div className="text-xs text-gray-500">Combine images with audio to create a stunning collage</div>
                        </div>
                    </div>

                    {/* Main Content - Reduced gap */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
                        {/* Left Column - Form */}
                        <div className="lg:col-span-2 space-y-4 md:space-y-6">
                            {/* Form - Reduced padding */}
                            <div className="bg-white rounded-lg shadow-md p-3 md:p-4">
                                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                                    <div className="flex justify-between gap-2">
                                        {/* Company Name */}
                                        <div className="w-full">
                                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                                Company Name *
                                            </label>
                                            <input
                                                type="text"
                                                {...register("companyName", {
                                                    required: "Company name is required",
                                                    minLength: {
                                                        value: 2,
                                                        message: "Must be at least 2 characters"
                                                    },
                                                    maxLength: {
                                                        value: 100,
                                                        message: "Must not exceed 100 characters"
                                                    }
                                                })}
                                                disabled={isLoading}
                                                className={`w-full px-3 py-2 border rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none transition ${errors.companyName ? 'border-red-300' : 'border-gray-300'}`}
                                                placeholder="Enter company name"
                                            />
                                            {errors.companyName && (
                                                <p className="mt-1 text-xs text-red-600">{errors.companyName.message}</p>
                                            )}
                                        </div>

                                        {/* Email */}
                                        <div className="w-full">
                                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                                Email Address
                                            </label>
                                            <input
                                                type="email"
                                                {...register("email", {
                                                    pattern: {
                                                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                                        message: "Invalid email address"
                                                    }
                                                })}
                                                disabled={isLoading}
                                                className={`w-full px-3 py-2 border rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none transition ${errors.email ? 'border-red-300' : 'border-gray-300'}`}
                                                placeholder="Enter email for updates"
                                            />
                                            {errors.email && (
                                                <p className="mt-1 text-xs text-red-600">{errors.email.message}</p>
                                            )}
                                        </div>
                                    </div>
                                    <div className="w-full">
                                        <label className="block text-xs font-medium text-gray-700 mb-2">
                                            Do you want the collage images to play in a loop? *
                                        </label>

                                        <div className="flex gap-4">
                                            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                                                <input
                                                    type="radio"
                                                    value={true}
                                                    {...register("isRepetative", { required: "Please select an option" })}
                                                    disabled={isLoading}
                                                    className="accent-orange-600"
                                                />
                                                Yes
                                            </label>

                                            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                                                <input
                                                    type="radio"
                                                    value={false}
                                                    {...register("isRepetative", { required: "Please select an option" })}
                                                    disabled={isLoading}
                                                    className="accent-orange-600"
                                                />
                                                No
                                            </label>
                                        </div>

                                        {errors.isRepetative && (
                                            <p className="mt-1 text-xs text-red-600">
                                                {errors.isRepetative.message}
                                            </p>
                                        )}
                                    </div>


                                    {/* Images Upload Section */}
                                    <div className="space-y-3">
                                        {/* images */}
                                        <div>
                                            <div className="flex items-center justify-between mb-1">
                                                <label className="block text-xs font-medium text-gray-700">
                                                    Images ({images.length}/25) *
                                                </label>
                                                <div className="flex items-center gap-1">
                                                    <AlertTriangle className="w-3 h-3 text-amber-500" />
                                                    <span className={`text-xs ${images.length >= 5 ? 'text-green-600' : 'text-amber-600'}`}>
                                                        {images.length >= 5 ? '✓ Ready' : `${5 - images.length} more`}
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Image Upload Instructions - Compact */}
                                            <div className="bg-blue-50 border border-blue-100 rounded-md p-2 mb-2">
                                                <div className="flex items-start gap-1">
                                                    <Info className="w-3 h-3 text-blue-600 mt-0.5 flex-shrink-0" />
                                                    <div>
                                                        <p className="text-xs font-medium text-blue-800 mb-0.5">Requirements:</p>
                                                        <ul className="text-[10px] text-blue-700">
                                                            <li>• JPG, JPEG, PNG, WEBP, HEIC, HEIF</li>
                                                            <li>• 5-25 images • &lt; 20MB each</li>
                                                        </ul>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Upload Area - Compact */}
                                            <div className="border-2 border-dashed border-gray-300 rounded-md p-4 text-center hover:border-blue-400 transition-colors">
                                                <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                                                <p className="text-[10px] text-gray-500 mb-3">
                                                    JPG, JPEG, PNG, WEBP, HEIC, HEIF
                                                </p>
                                                <input
                                                    type="file"
                                                    id="image-upload"
                                                    multiple
                                                    accept=".jpg,.jpeg,.png,.webp,.heic,.heif"
                                                    onChange={handleImageUpload}
                                                    className="hidden"
                                                    disabled={isLoading}
                                                />
                                                <label
                                                    htmlFor="image-upload"
                                                    className="inline-block px-4 py-2 bg-gray-800 text-white text-xs font-medium rounded-md hover:bg-gray-900 cursor-pointer transition-colors"
                                                >
                                                    Select Images
                                                </label>
                                            </div>

                                            {/* Uploaded Images Preview - Smaller thumbnails */}
                                            {images.length > 0 && (
                                                <div className="mt-3">
                                                    <div className="flex items-center justify-between mb-2">
                                                        <h4 className="text-xs font-medium text-gray-700">
                                                            Uploaded ({images.length})
                                                        </h4>
                                                        <button
                                                            type="button"
                                                            onClick={clearAllImages}
                                                            className="text-xs text-red-600 hover:text-red-700 font-medium"
                                                        >
                                                            Clear All
                                                        </button>
                                                    </div>
                                                    <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-2">
                                                        {images.map((image) => (
                                                            <div key={image.id} className="relative group">
                                                                <div className="aspect-square rounded-md overflow-hidden bg-gray-100 border border-gray-200">
                                                                    <img
                                                                        src={image.preview}
                                                                        alt={image.name}
                                                                        className="w-full h-full object-cover"
                                                                    />
                                                                </div>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => removeImage(image.id)}
                                                                    className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors shadow-md z-10 text-xs"
                                                                >
                                                                    <X className="w-2.5 h-2.5" />
                                                                </button>
                                                                <div className="mt-1">
                                                                    <p className="text-[10px] text-gray-600 truncate">{image.name}</p>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {/* brand logo */}
                                        <div>
                                            <div className="flex items-center justify-between mb-1">
                                                <label className="block text-xs font-medium text-gray-700">
                                                    Brand Logo
                                                </label>
                                            </div>

                                            {/* Image Upload Instructions - Compact */}
                                            <div className="bg-blue-50 border border-blue-100 rounded-md p-2 mb-2">
                                                <div className="flex items-start gap-1">
                                                    <Info className="w-3 h-3 text-blue-600 mt-0.5 flex-shrink-0" />
                                                    <div>
                                                        <p className="text-xs font-medium text-blue-800 mb-0.5">Requirements:</p>
                                                        <ul className="text-[10px] text-blue-700">
                                                            <li>• JPG, JPEG, PNG, WEBP, HEIC, HEIF</li>
                                                            <li>• 1 image • &lt; 20MB</li>
                                                        </ul>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Upload Area - Compact */}
                                            <div className="border-2 border-dashed border-gray-300 rounded-md p-4 text-center hover:border-blue-400 transition-colors">
                                                <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                                                <p className="text-[10px] text-gray-500 mb-3">
                                                    JPG, JPEG, PNG, WEBP, HEIC, HEIF
                                                </p>
                                                <input
                                                    type="file"
                                                    id="brand-image"
                                                    // multiple
                                                    accept=".jpg,.jpeg,.png,.webp,.heic,.heif"
                                                    onChange={handleBrandLogoUpload}
                                                    className="hidden"
                                                    disabled={isLoading}
                                                />
                                                <label
                                                    htmlFor="brand-image"
                                                    className="inline-block px-4 py-2 bg-gray-800 text-white text-xs font-medium rounded-md hover:bg-gray-900 cursor-pointer transition-colors"
                                                >
                                                    Upload Brand Logo
                                                </label>
                                            </div>

                                            {/* Uploaded Images Preview - Smaller thumbnails */}
                                            {brandLogo.length > 0 && (
                                                <div className="mt-3">
                                                    <div className="flex items-center justify-between mb-2">
                                                        <h4 className="text-xs font-medium text-gray-700">
                                                            Uploaded ({images.length})
                                                        </h4>
                                                        <button
                                                            type="button"
                                                            onClick={clearLogo}
                                                            className="text-xs text-red-600 hover:text-red-700 font-medium"
                                                        >
                                                            Clear All
                                                        </button>
                                                    </div>
                                                    <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-2">
                                                        {brandLogo.map((image) => (
                                                            <div key={image.id} className="relative group">
                                                                <div className="aspect-square rounded-md overflow-hidden bg-gray-100 border border-gray-200">
                                                                    <img
                                                                        src={image.preview}
                                                                        alt={image.name}
                                                                        className="w-full h-full object-cover"
                                                                    />
                                                                </div>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => removeLogo(image.id)}
                                                                    className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors shadow-md z-10 text-xs"
                                                                >
                                                                    <X className="w-2.5 h-2.5" />
                                                                </button>
                                                                <div className="mt-1">
                                                                    <p className="text-[10px] text-gray-600 truncate">{image.name}</p>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {/* Audio Upload Section */}
                                        <div className="space-y-3">
                                            <div>
                                                <div className="flex items-center justify-between mb-1">
                                                    <label className="block text-xs font-medium text-gray-700">
                                                        Background Audio *
                                                    </label>
                                                    <div className="flex items-center gap-1">
                                                        <AlertTriangle className="w-3 h-3 text-amber-500" />
                                                        <span className={`text-xs ${audioFile ? 'text-green-600' : 'text-amber-600'}`}>
                                                            {audioFile ? '✓ Uploaded' : 'Required'}
                                                        </span>
                                                    </div>
                                                </div>

                                                {/* Audio Upload Instructions - Compact */}
                                                <div className="bg-blue-50 border border-blue-100 rounded-md p-2 mb-2">
                                                    <div className="flex items-start gap-1">
                                                        <Info className="w-3 h-3 text-blue-600 mt-0.5 flex-shrink-0" />
                                                        <div>
                                                            <p className="text-xs font-medium text-blue-800 mb-0.5">Requirements:</p>
                                                            <ul className="text-[10px] text-blue-700">
                                                                <li>• MP3, WAV • Max 30MB</li>
                                                                <li>• Clear audio recommended</li>
                                                            </ul>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Upload Area - Compact */}
                                                <div className="border-2 border-dashed border-gray-300 rounded-md p-4 text-center hover:border-blue-400 transition-colors">
                                                    <Music className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                                                    <p className="text-xs text-gray-600 mb-3">
                                                        Upload background audio
                                                    </p>
                                                    <input
                                                        type="file"
                                                        id="audio-upload"
                                                        accept=".mp3,.wav,.mpga"
                                                        onChange={handleAudioUpload}
                                                        className="hidden"
                                                        disabled={isLoading}
                                                    />
                                                    <label
                                                        htmlFor="audio-upload"
                                                        className="inline-block px-4 py-2 bg-gray-800 text-white text-xs font-medium rounded-md hover:bg-gray-900 cursor-pointer transition-colors"
                                                    >
                                                        Select Audio
                                                    </label>
                                                </div>

                                                {/* Uploaded Audio Preview - Compact */}
                                                {audioFile && (
                                                    <div className="mt-3">
                                                        <div className="bg-gray-50 rounded-md p-3 border border-gray-200">
                                                            <div className="flex items-center justify-between">
                                                                <div className="flex-1 min-w-0">
                                                                    <div className="flex items-center gap-1.5 mb-0.5">
                                                                        <Music className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />
                                                                        <p className="text-xs font-medium text-gray-800 truncate">
                                                                            {audioFile.name}
                                                                        </p>
                                                                    </div>
                                                                    <p className="text-[10px] text-gray-500">Size: {audioFile.size}</p>
                                                                </div>
                                                                <button
                                                                    type="button"
                                                                    onClick={removeAudio}
                                                                    className="text-gray-400 hover:text-red-500 transition-colors ml-2 flex-shrink-0"
                                                                >
                                                                    <X className="w-4 h-4" />
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Submit Button */}
                                        <div className="pt-3 flex items-center justify-center">
                                            <button
                                                type="submit"
                                                disabled={isLoading || images.length < 5 || !audioFile}
                                                className="flex items-center px-3 py-2 text-sm bg-orange-600 text-white rounded hover:bg-orange-700  gap-1"
                                            >
                                                {isLoading ? (
                                                    <>
                                                        <Loader2 className="w-4 h-4 animate-spin" />
                                                        Creating...
                                                    </>
                                                ) : (
                                                    <>
                                                        <CheckCircle className="w-4 h-4" />
                                                        Create Collage
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>

                        {/* Right Column - Orders List */}
                        <div className="space-y-4 md:space-y-6">
                            {/* Search and Filter Card - Reduced padding */}
                            <div className="bg-white rounded-lg shadow-md p-4">
                                <div className="flex items-center justify-between mb-3">
                                    <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-1.5">
                                        <Search className="w-4 h-4 text-blue-600" />
                                        Search Orders
                                    </h3>

                                    <button
                                        type="button"
                                        onClick={handleRefresh}
                                        disabled={isLoadingOrders}
                                        className="flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-md bg-gray-100 hover:bg-gray-200 transition disabled:opacity-50"
                                    >
                                        <RefreshCw className={`w-3.5 h-3.5 ${isLoadingOrders ? "animate-spin" : ""}`} />
                                        Refresh
                                    </button>
                                </div>


                                <div className="space-y-3">
                                    <div className="relative">
                                        <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                        <input
                                            type="text"
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            placeholder="Search by company or email..."
                                            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none transition text-sm"
                                        />
                                    </div>

                                    <div className="flex items-center justify-between text-xs">
                                        <span className="text-gray-600">
                                            {filteredOrders.length} of {orders.length} orders
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Orders List Card - Reduced padding */}
                            <div className="bg-white rounded-lg shadow-md p-4">
                                <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-1.5">
                                    <ListOrdered className="w-4 h-4 text-purple-600" />
                                    Recent Orders
                                </h3>

                                {loading ? (
                                    <div className="flex justify-center items-center py-6">
                                        <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
                                    </div>
                                ) : filteredOrders.length === 0 ? (
                                    <div className="text-center py-6">
                                        <FolderOpen className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                                        <p className="text-sm text-gray-500">No orders found</p>
                                    </div>
                                ) : (
                                    <div className="space-y-3 max-h-[350px] overflow-y-auto pr-1">
                                        {filteredOrders.reverse().map((order) => (
                                            <div
                                                key={order.order_id}
                                                className="border border-gray-200 rounded-md p-3 hover:border-blue-200 hover:bg-blue-50 transition-colors"
                                            >
                                                <div className="flex justify-between items-start mb-2">
                                                    <div className="flex-1 min-w-0 pr-2">
                                                        <h4 className="font-medium text-gray-800 truncate text-sm">
                                                            {order.company_name}
                                                        </h4>
                                                        <p className="text-xs text-gray-600 truncate mt-0.5">
                                                            {order.email_id}
                                                        </p>
                                                    </div>
                                                    <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${order.status === 'completed' ? 'bg-green-100 text-green-800' :
                                                        order.status === 'processing' ? 'bg-blue-100 text-blue-800' :
                                                            order.status === 'failed' ? 'bg-red-100 text-red-800' :
                                                                'bg-gray-100 text-gray-800'
                                                        }`}>
                                                        {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                                                    </span>
                                                </div>

                                                <div className="flex items-center justify-between text-xs">
                                                    <div className="flex items-center gap-2">
                                                        <div className="flex items-center gap-0.5">
                                                            <Image className="w-3.5 h-3.5 text-gray-500" />
                                                            <span className="text-gray-600">{order.images?.length || 0}</span>
                                                        </div>
                                                        <div className="flex items-center gap-0.5">
                                                            <Music className="w-3.5 h-3.5 text-gray-500" />
                                                            <span className="text-gray-600">Audio</span>
                                                        </div>
                                                    </div>

                                                    <button
                                                        onClick={() => navigate(`/collage-delivery/${order.order_id}`)}
                                                        disabled={order.status !== 'completed'}
                                                        className={`text-xs font-medium px-2 py-1 rounded-md transition-colors flex items-center gap-0.5 ${order.status === 'completed'
                                                            ? 'bg-blue-600 text-white hover:bg-blue-700'
                                                            : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                                            }`}
                                                    >
                                                        <Download className="w-3.5 h-3.5" />
                                                        Get
                                                    </button>
                                                </div>

                                                <div className="mt-2 pt-2 border-t border-gray-100">
                                                    <div className="text-[10px] text-gray-500">
                                                        ID: {order.order_id}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Statistics Card - Reduced padding */}
                            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100 rounded-lg p-4 shadow-md">
                                <h3 className="font-medium text-gray-800 mb-3 flex items-center gap-1.5">
                                    <BarChart3 className="w-4 h-4 text-blue-600" />
                                    Statistics
                                </h3>
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="text-center">
                                        <div className="text-lg font-bold text-blue-600">
                                            {orders.filter(o => o.status === 'completed').length}
                                        </div>
                                        <div className="text-xs text-gray-600 mt-0.5">Completed</div>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-lg font-bold text-blue-600">
                                            {orders.filter(o => o.status === 'processing').length}
                                        </div>
                                        <div className="text-xs text-gray-600 mt-0.5">Processing</div>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-lg font-bold text-blue-600">
                                            {orders.filter(o => o.status === 'failed').length}
                                        </div>
                                        <div className="text-xs text-gray-600 mt-0.5">Failed</div>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-lg font-bold text-blue-600">
                                            {orders.length}
                                        </div>
                                        <div className="text-xs text-gray-600 mt-0.5">Total</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default CollageCreationPage;